import { ref } from 'vue'
import { useWeb3 } from './useWeb3'
import { useLocalKeys } from './useLocalKeys'
import { useJupStore } from '@/stores/jupiterStore'
import { useMyToast } from './useMyToast'
import { i18n } from '@/i18n'
import { CHAIN_RPCS } from '@/utils/consts'
import { ALLOWED_QUOTE_MINTS } from '@/utils/dexPoolConsts'
import {
  detectDexPoolType,
  getDexNameForRouter,
  getJupiterDexName,
  getUsdtAddress,
  V2_ROUTER_ADDRESSES,
  V3_SWAP_ROUTER_ADDRESSES,
  V3_QUOTER_ADDRESSES,
  V2_ROUTER_ABI,
  V2_ROUTER_VIEW_ABI,
  V3_SWAP_ROUTER_ABI,
  V3_QUOTER_ABI,
  V3_POOL_ABI,
  ERC20_APPROVE_ABI,
} from '@/utils/dexPoolSwapConsts'
import { toBigInt } from '@/utils'
import type { ComputedPool } from './usePoolsData'
import { Keypair } from '@solana/web3.js'
import bs58 from 'bs58'

const { t } = i18n.global

interface TokenOrder {
  baseToken: string
  quoteToken: string
  baseDec: number
  quoteDec: number
}

function getTokenOrderV2(state: Record<string, unknown>, chain: string): TokenOrder | null {
  const token0 = typeof state.token0 === 'string' ? state.token0.toLowerCase() : null
  const token1 = typeof state.token1 === 'string' ? state.token1.toLowerCase() : null
  const dec0 = Number(state.dec0 ?? 0)
  const dec1 = Number(state.dec1 ?? 0)
  if (!token0 || !token1 || !dec0 || !dec1) return null

  const allowed = ALLOWED_QUOTE_MINTS[chain.toLowerCase()]
  const isQuote0 = allowed?.has(token0) ?? false
  const isQuote1 = allowed?.has(token1) ?? false

  if (isQuote0 && !isQuote1) {
    return { baseToken: token1, quoteToken: token0, baseDec: dec1, quoteDec: dec0 }
  }
  if (isQuote1 && !isQuote0) {
    return { baseToken: token0, quoteToken: token1, baseDec: dec0, quoteDec: dec1 }
  }
  return null
}

function getTokenOrderV3(state: Record<string, unknown>, chain: string): TokenOrder | null {
  return getTokenOrderV2(state, chain)
}

function getTokenOrderSol(state: Record<string, unknown>, chain: string): TokenOrder | null {
  const c = chain.toLowerCase()
  const allowed = ALLOWED_QUOTE_MINTS[c]

  let a = typeof state.mint_x === 'string' ? state.mint_x.toLowerCase() : null
  let b = typeof state.mint_y === 'string' ? state.mint_y.toLowerCase() : null
  let decA = Number(state.dec_x ?? 0)
  let decB = Number(state.dec_y ?? 0)

  if (!a || !b) {
    a = typeof state.coin === 'string' ? state.coin.toLowerCase() : null
    b = typeof state.pc === 'string' ? state.pc.toLowerCase() : null
    decA = Number(state.coin_dec ?? 0)
    decB = Number(state.pc_dec ?? 0)
  }

  if (!a || !b) {
    a = typeof state.mint0 === 'string' ? state.mint0.toLowerCase() : null
    b = typeof state.mint1 === 'string' ? state.mint1.toLowerCase() : null
    decA = Number(state.mint0_dec ?? 0)
    decB = Number(state.mint1_dec ?? 0)
  }

  if (!a || !b) {
    a = typeof state.base === 'string' ? state.base.toLowerCase() : null
    b = typeof state.quote === 'string' ? state.quote.toLowerCase() : null
    decA = Number(state.quote_dec ?? 6)
    decB = Number(state.base_dec ?? 9)
  }

  if (!a || !b || !decA || !decB) return null

  const isQuoteA = allowed?.has(a) ?? false
  const isQuoteB = allowed?.has(b) ?? false

  if (isQuoteA && !isQuoteB) {
    return { baseToken: b, quoteToken: a, baseDec: decB, quoteDec: decA }
  }
  if (isQuoteB && !isQuoteA) {
    return { baseToken: a, quoteToken: b, baseDec: decA, quoteDec: decB }
  }

  const sol = 'so11111111111111111111111111111111111111112'
  const isASol = a === sol
  const isBSol = b === sol
  if (isASol && !isBSol) {
    return { baseToken: b, quoteToken: a, baseDec: decB, quoteDec: decA }
  }
  if (isBSol && !isASol) {
    return { baseToken: a, quoteToken: b, baseDec: decA, quoteDec: decB }
  }

  return null
}

function getAmountOutV2(amountIn: bigint, reserveIn: bigint, reserveOut: bigint): bigint {
  const amountInWithFee = amountIn * 997n
  const numerator = amountInWithFee * reserveOut
  const denominator = reserveIn * 1000n + amountInWithFee
  return denominator === 0n ? 0n : numerator / denominator
}

export function useDexPoolSwap() {
  const loading = ref(false)
  const error = ref<string | null>(null)
  const localKeys = useLocalKeys()
  const jupStore = useJupStore()
  const { addToast } = useMyToast()

  const getEvmKeys = (chain: string) => {
    const privateKey = localKeys.get('oneinch-key-evm')
    if (!privateKey) throw new Error('EVM private key not found')
    const { initWeb3, getPublicKey } = useWeb3()
    const rpcs = CHAIN_RPCS[chain.toLowerCase()]
    if (!rpcs?.length) throw new Error('Unsupported chain')
    initWeb3(rpcs)
    const publicKey = getPublicKey(privateKey)
    return { privateKey, publicKey }
  }

  const getSolKeys = () => {
    const apiKey = localKeys.get('jup-api')
    const privateKey = localKeys.get('jup-xprv')
    if (!apiKey || !privateKey) throw new Error('Jupiter keys not found')
    jupStore.apiKey = apiKey
    const wallet = Keypair.fromSecretKey(bs58.decode(privateKey))
    return { privateKey, publicKey: wallet.publicKey.toBase58() }
  }

  const getWeb3 = async (chain: string) => {
    const rpcs = CHAIN_RPCS[chain.toLowerCase()]
    if (!rpcs?.length) throw new Error('Unsupported chain')
    const { Web3 } = await import('web3')
    return new Web3(rpcs[0])
  }

  const approveErc20 = async (
    chain: string,
    token: string,
    spender: string,
    amount: string,
    privateKey: string,
    publicKey: string,
  ) => {
    const { sendRaw } = useWeb3()
    const web3 = await getWeb3(chain)
    const contract = new web3.eth.Contract(ERC20_APPROVE_ABI as any, token)
    const data = (contract.methods as any).approve(spender, amount.toString()).encodeABI()
    const gasPrice = await web3.eth.getGasPrice()
    const receipt = await sendRaw(privateKey, publicKey, token, data, gasPrice.toString(), null, '0')
    return receipt
  }

  const checkAllowance = async (
    chain: string,
    token: string,
    owner: string,
    spender: string,
  ): Promise<bigint> => {
    const web3 = await getWeb3(chain)
    const contract = new web3.eth.Contract([
      {
        constant: true,
        inputs: [
          { name: '_owner', type: 'address' },
          { name: '_spender', type: 'address' },
        ],
        name: 'allowance',
        outputs: [{ name: '', type: 'uint256' }],
        payable: false,
        stateMutability: 'view',
        type: 'function',
      },
    ] as any, token)
    const allowance = await (contract.methods as any).allowance(owner, spender).call()
    return BigInt(allowance as unknown as string)
  }

  // ─── QUOTE functions (simulate before swap) ───

  const quoteEvmV2 = async (
    pool: ComputedPool,
    chain: string,
    side: 'buy' | 'sell',
    amountUsd: number,
  ): Promise<{ amountIn: bigint; amountOut: bigint; path: string[] }> => {
    const order = getTokenOrderV2(pool.state ?? {}, chain)
    if (!order) throw new Error('Cannot determine token order for V2 pool')

    const { baseToken, quoteToken, baseDec, quoteDec } = order
    const dexName = getDexNameForRouter(pool.dexParser ?? pool.dexId, chain)
    if (!dexName) throw new Error('Unknown DEX for V2 router')

    const routerAddress = V2_ROUTER_ADDRESSES[chain.toLowerCase()]?.[dexName]
    if (!routerAddress) throw new Error(`V2 Router not found for ${dexName} on ${chain}`)

    const web3 = await getWeb3(chain)
    const usdt = getUsdtAddress(chain)
    const needsHop = usdt && quoteToken.toLowerCase() !== usdt.toLowerCase()

    let amountIn: bigint
    let path: string[]

    if (side === 'buy') {
      amountIn = toBigInt(amountUsd * Math.pow(10, quoteDec))
      path = needsHop ? [usdt!, quoteToken, baseToken] : [quoteToken, baseToken]
    } else {
      const tokenAmount = amountUsd / (pool.spotPrice ?? 1)
      amountIn = toBigInt(tokenAmount * Math.pow(10, baseDec))
      path = needsHop ? [baseToken, quoteToken, usdt!] : [baseToken, quoteToken]
    }

    const router = new web3.eth.Contract(V2_ROUTER_VIEW_ABI as any, routerAddress)
    const amounts = await (router.methods as any).getAmountsOut(amountIn.toString(), path).call()
    const outArr = amounts as unknown as string[]
    const last = outArr[outArr.length - 1]
    if (!last) throw new Error('Quote returned empty amounts array')
    const amountOut = BigInt(last)

    return { amountIn, amountOut, path }
  }

  const quoteEvmV3 = async (
    pool: ComputedPool,
    chain: string,
    side: 'buy' | 'sell',
    amountUsd: number,
  ): Promise<{ amountIn: bigint; amountOut: bigint }> => {
    const order = getTokenOrderV3(pool.state ?? {}, chain)
    if (!order) throw new Error('Cannot determine token order for V3 pool')

    const { baseToken, quoteToken, baseDec, quoteDec } = order
    const dexName = getDexNameForRouter(pool.dexParser ?? pool.dexId, chain)
    if (!dexName) throw new Error('Unknown DEX for V3 router')

    const usdt = getUsdtAddress(chain)
    if (usdt && quoteToken.toLowerCase() !== usdt.toLowerCase()) {
      throw new Error(
        `V3 direct swap requires ${quoteToken.toUpperCase()} on balance. ` +
          `Please swap USDT to ${quoteToken.toUpperCase()} first, or use a USDT-quoted pool.`,
      )
    }

    const quoterAddress = V3_QUOTER_ADDRESSES[chain.toLowerCase()]?.[dexName]
    if (!quoterAddress) throw new Error(`V3 Quoter not found for ${dexName} on ${chain}`)

    const web3 = await getWeb3(chain)
    const poolContract = new web3.eth.Contract(V3_POOL_ABI as any, pool.address)
    const fee = await (poolContract.methods as any).fee().call()
    const feeNum = Number(fee as unknown as string)

    let amountIn: bigint
    let tokenIn: string
    let tokenOut: string

    if (side === 'buy') {
      tokenIn = quoteToken
      tokenOut = baseToken
      amountIn = toBigInt(amountUsd * Math.pow(10, quoteDec))
    } else {
      tokenIn = baseToken
      tokenOut = quoteToken
      const tokenAmount = amountUsd / (pool.spotPrice ?? 1)
      amountIn = toBigInt(tokenAmount * Math.pow(10, baseDec))
    }

    const quoter = new web3.eth.Contract(V3_QUOTER_ABI as any, quoterAddress)
    const amountOut = await (quoter.methods as any)
      .quoteExactInputSingle(tokenIn, tokenOut, feeNum, amountIn.toString(), '0')
      .call()

    return { amountIn, amountOut: BigInt(amountOut as unknown as string) }
  }

  const quoteSolana = async (
    pool: ComputedPool,
    side: 'buy' | 'sell',
    amountUsd: number,
  ): Promise<{ amountIn: bigint; amountOut: bigint }> => {
    const { publicKey } = getSolKeys()
    const order = getTokenOrderSol(pool.state ?? {}, 'sol')
    if (!order) throw new Error('Cannot determine token order for Solana pool')

    const { baseToken, quoteToken, baseDec, quoteDec } = order
    const dexName = getJupiterDexName(pool.dexParser ?? pool.dexId)

    let inputMint: string
    let outputMint: string
    let amountRaw: number

    if (side === 'buy') {
      inputMint = quoteToken
      outputMint = baseToken
      amountRaw = Math.round(amountUsd * Math.pow(10, quoteDec))
    } else {
      inputMint = baseToken
      outputMint = quoteToken
      const tokenAmount = amountUsd / (pool.spotPrice ?? 1)
      amountRaw = Math.round(tokenAmount * Math.pow(10, baseDec))
    }

    const orderRes = await jupStore.getOrder(inputMint, outputMint, amountRaw, publicKey, dexName ?? undefined)
    if (!orderRes || 'error' in orderRes) {
      const errMsg = (orderRes as unknown as Record<string, string>)?.error || 'Jupiter quote failed'
      throw new Error(errMsg)
    }
    const res = orderRes as { inAmount: string; outAmount: string }
    return { amountIn: BigInt(res.inAmount), amountOut: BigInt(res.outAmount) }
  }

  const quotePool = async (
    pool: ComputedPool,
    chain: string,
    side: 'buy' | 'sell',
    amountUsd: number,
  ): Promise<{ amountIn: bigint; amountOut: bigint; path?: string[] }> => {
    const dexType = detectDexPoolType(pool.dexParser ?? pool.dexId)
    if (dexType === 'evm-v2') {
      return quoteEvmV2(pool, chain, side, amountUsd)
    }
    if (dexType === 'evm-v3') {
      return quoteEvmV3(pool, chain, side, amountUsd)
    }
    if (dexType === 'solana-jupiter') {
      return quoteSolana(pool, side, amountUsd)
    }
    throw new Error(`Quote not supported for pool type: ${pool.dexParser ?? pool.dexId}`)
  }

  // ─── SWAP functions ───

  const swapEvmV2 = async (
    pool: ComputedPool,
    chain: string,
    side: 'buy' | 'sell',
    amountUsd: number,
    slippagePercent: number,
  ): Promise<string> => {
    const { privateKey, publicKey } = getEvmKeys(chain)
    const { amountIn, amountOut, path } = await quoteEvmV2(pool, chain, side, amountUsd)

    const dexName = getDexNameForRouter(pool.dexParser ?? pool.dexId, chain)
    if (!dexName) throw new Error('Unknown DEX for V2 router')
    const routerAddress = V2_ROUTER_ADDRESSES[chain.toLowerCase()]?.[dexName]
    if (!routerAddress) throw new Error(`V2 Router not found for ${dexName} on ${chain}`)

    const web3 = await getWeb3(chain)
    const amountOutMin = (amountOut * BigInt(Math.round((100 - slippagePercent) * 100))) / 10000n

    // Check allowance on the first token in path (tokenIn)
    const tokenIn = path[0]!
    const allowance = await checkAllowance(chain, tokenIn, publicKey, routerAddress)
    if (allowance < amountIn) {
      await approveErc20(chain, tokenIn, routerAddress, (amountIn * 2n).toString(), privateKey, publicKey)
    }

    const deadline = Math.floor(Date.now() / 1000) + 300
    const router = new web3.eth.Contract(V2_ROUTER_ABI as any, routerAddress)
    const data = (router.methods as any).swapExactTokensForTokens(
      amountIn.toString(),
      amountOutMin.toString(),
      path,
      publicKey,
      deadline,
    ).encodeABI()

    const { sendRaw } = useWeb3()
    const gasPrice = await web3.eth.getGasPrice()
    const receipt = await sendRaw(privateKey, publicKey, routerAddress, data, gasPrice.toString(), null, '0')
    return (receipt as any)?.transactionHash ?? ''
  }

  const swapEvmV3 = async (
    pool: ComputedPool,
    chain: string,
    side: 'buy' | 'sell',
    amountUsd: number,
    slippagePercent: number,
  ): Promise<string> => {
    const { privateKey, publicKey } = getEvmKeys(chain)
    const { amountIn, amountOut } = await quoteEvmV3(pool, chain, side, amountUsd)

    const dexName = getDexNameForRouter(pool.dexParser ?? pool.dexId, chain)
    if (!dexName) throw new Error('Unknown DEX for V3 router')
    const routerAddress = V3_SWAP_ROUTER_ADDRESSES[chain.toLowerCase()]?.[dexName]
    if (!routerAddress) throw new Error(`V3 Router not found for ${dexName} on ${chain}`)

    const web3 = await getWeb3(chain)
    const poolContract = new web3.eth.Contract(V3_POOL_ABI as any, pool.address)
    const fee = await (poolContract.methods as any).fee().call()
    const feeNum = Number(fee as unknown as string)

    const order = getTokenOrderV3(pool.state ?? {}, chain)
    if (!order) throw new Error('Cannot determine token order for V3 pool')
    const { baseToken, quoteToken } = order

    const tokenIn = side === 'buy' ? quoteToken : baseToken
    const tokenOut = side === 'buy' ? baseToken : quoteToken
    const amountOutMin = (amountOut * BigInt(Math.round((100 - slippagePercent) * 100))) / 10000n

    const allowance = await checkAllowance(chain, tokenIn, publicKey, routerAddress)
    if (allowance < amountIn) {
      await approveErc20(chain, tokenIn, routerAddress, (amountIn * 2n).toString(), privateKey, publicKey)
    }

    const router = new web3.eth.Contract(V3_SWAP_ROUTER_ABI as any, routerAddress)
    const params = {
      tokenIn,
      tokenOut,
      fee: feeNum,
      recipient: publicKey,
      amountIn: amountIn.toString(),
      amountOutMinimum: amountOutMin.toString(),
      sqrtPriceLimitX96: '0',
    }
    const data = (router.methods as any).exactInputSingle(params).encodeABI()

    const { sendRaw } = useWeb3()
    const gasPrice = await web3.eth.getGasPrice()
    const receipt = await sendRaw(privateKey, publicKey, routerAddress, data, gasPrice.toString(), null, '0')
    return (receipt as any)?.transactionHash ?? ''
  }

  const swapSolana = async (
    pool: ComputedPool,
    side: 'buy' | 'sell',
    amountUsd: number,
    slippagePercent: number,
  ): Promise<string> => {
    const { publicKey } = getSolKeys()
    const order = getTokenOrderSol(pool.state ?? {}, 'sol')
    if (!order) throw new Error('Cannot determine token order for Solana pool')

    const { baseToken, quoteToken, baseDec, quoteDec } = order
    const dexName = getJupiterDexName(pool.dexParser ?? pool.dexId)

    let inputMint: string
    let outputMint: string
    let amountRaw: number

    if (side === 'buy') {
      inputMint = quoteToken
      outputMint = baseToken
      amountRaw = Math.round(amountUsd * Math.pow(10, quoteDec))
    } else {
      inputMint = baseToken
      outputMint = quoteToken
      const tokenAmount = amountUsd / (pool.spotPrice ?? 1)
      amountRaw = Math.round(tokenAmount * Math.pow(10, baseDec))
    }

    const slippageBps = Math.round(slippagePercent * 100)
    const orderRes = await jupStore.getOrder(inputMint, outputMint, amountRaw, publicKey, dexName ?? undefined, slippageBps)
    if (!orderRes || 'error' in orderRes) {
      const errMsg = (orderRes as unknown as Record<string, string>)?.error || 'Jupiter order failed'
      throw new Error(errMsg)
    }

    const { Keypair, VersionedTransaction } = await import('@solana/web3.js')
    const privateKey = localKeys.get('jup-xprv')!
    const wallet = Keypair.fromSecretKey(bs58.decode(privateKey))

    const serialized = Buffer.from((orderRes as any).transaction, 'base64')
    const vtx = VersionedTransaction.deserialize(serialized)
    vtx.sign([wallet])
    const signedTx = Buffer.from(vtx.serialize()).toString('base64')

    const result = await jupStore.execute(signedTx, (orderRes as any).requestId)
    return (result as any)?.signature ?? ''
  }

  const swapPool = async (
    pool: ComputedPool,
    chain: string,
    side: 'buy' | 'sell',
    amountUsd: number,
    slippagePercent: number,
  ): Promise<{ txHash: string }> => {
    loading.value = true
    error.value = null
    try {
      const dexType = detectDexPoolType(pool.dexParser ?? pool.dexId)
      let txHash = ''

      if (dexType === 'evm-v2') {
        txHash = await swapEvmV2(pool, chain, side, amountUsd, slippagePercent)
      } else if (dexType === 'evm-v3') {
        txHash = await swapEvmV3(pool, chain, side, amountUsd, slippagePercent)
      } else if (dexType === 'solana-jupiter') {
        txHash = await swapSolana(pool, side, amountUsd, slippagePercent)
      } else {
        throw new Error(`Unsupported pool type: ${pool.dexParser ?? pool.dexId}`)
      }

      return { txHash }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      error.value = msg
      addToast({ severity: 'error', message: msg })
      throw e
    } finally {
      loading.value = false
    }
  }

  const executeArbitrage = async (
    buyPool: ComputedPool,
    sellPool: ComputedPool,
    chain: string,
    amountUsd: number,
    slippagePercent: number,
    minProfitPercent = 0,
  ) => {
    loading.value = true
    error.value = null
    try {
      // 1. Quote both pools
      const buyQuote = await quotePool(buyPool, chain, 'buy', amountUsd)
      const sellQuote = await quotePool(sellPool, chain, 'sell', amountUsd)

      const buyTokenAmount = Number(buyQuote.amountOut)
      const sellUsdtAmount = Number(sellQuote.amountOut)

      // Convert raw amounts to human-readable
      const buyOrder = getTokenOrderV2(buyPool.state ?? {}, chain) ?? getTokenOrderV3(buyPool.state ?? {}, chain)
      const sellOrder = getTokenOrderV2(sellPool.state ?? {}, chain) ?? getTokenOrderV3(sellPool.state ?? {}, chain)

      let buyTokenHuman = 0
      let sellUsdtHuman = 0

      if (buyOrder) {
        buyTokenHuman = buyTokenAmount / Math.pow(10, buyOrder.baseDec)
      }
      if (sellOrder) {
        sellUsdtHuman = sellUsdtAmount / Math.pow(10, sellOrder.quoteDec)
      }

      const profit = sellUsdtHuman - amountUsd
      const profitPercent = amountUsd > 0 ? (profit / amountUsd) * 100 : 0

      addToast({
        severity: 'info',
        message: `Quote: buy ${buyTokenHuman.toFixed(6)} ${buyPool.baseSymbol ?? 'token'}, sell → ${sellUsdtHuman.toFixed(2)} USDT. Profit: ${profitPercent.toFixed(2)}%`,
      })

      if (profitPercent < minProfitPercent) {
        throw new Error(
          `Arbitrage not profitable: expected profit ${profitPercent.toFixed(2)}% < min ${minProfitPercent}%`,
        )
      }

      // 2. Execute buy
      const buyResult = await swapPool(buyPool, chain, 'buy', amountUsd, slippagePercent)
      addToast({
        severity: 'success',
        message:
          t('modal.createSwapOrders.dexSwapSuccess', {
            amount: amountUsd,
            asset: buyPool.baseSymbol ?? 'token',
          }) + ` [tx: ${buyResult.txHash.slice(0, 10)}...]`,
      })

      // 3. Execute sell (using actual bought amount if possible, fallback to quote)
      const sellAmountUsd = sellUsdtHuman > 0 ? sellUsdtHuman : amountUsd
      const sellResult = await swapPool(sellPool, chain, 'sell', sellAmountUsd, slippagePercent)
      addToast({
        severity: 'success',
        message:
          t('modal.createSwapOrders.dexSwapSuccess', {
            amount: sellAmountUsd,
            asset: 'USDT',
          }) + ` [tx: ${sellResult.txHash.slice(0, 10)}...]`,
      })

      return { buyTxHash: buyResult.txHash, sellTxHash: sellResult.txHash, profitPercent }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      error.value = msg
      addToast({ severity: 'error', message: msg })
      throw e
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    swapPool,
    quotePool,
    executeArbitrage,
  }
}
